import React from 'react'
import styled from 'styled-components'

export interface ChipProps {
  children: React.ReactNode
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info'
  size?: 'small' | 'medium' | 'large'
  onClick?: () => void
  className?: string
}

const StyledChip = styled.span<ChipProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ size }) => {
    switch (size) {
      case 'small':
        return '2px 8px'
      case 'large':
        return '6px 14px'
      default:
        return '4px 10px'
    }
  }};
  font-size: ${({ size }) => {
    switch (size) {
      case 'small':
        return '10px'
      case 'large':
        return '13px'
      default:
        return '11px'
    }
  }};
  font-weight: 500;
  border-radius: 20px;
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
  transition: all 0.2s ease;
  user-select: none;
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ variant, onClick }) => {
    switch (variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
        `
      case 'secondary':
        return `
          background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(108, 117, 125, 0.3);
        `
      case 'success':
        return `
          background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
        `
      case 'warning':
        return `
          background: linear-gradient(135deg, #ffc107 0%, #fd7e14 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3);
        `
      case 'danger':
        return `
          background: linear-gradient(135deg, #dc3545 0%, #e83e8c 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
        `
      case 'info':
        return `
          background: linear-gradient(135deg, #17a2b8 0%, #6f42c1 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(23, 162, 184, 0.3);
        `
      default:
        return `
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
        `
    }
  }}

  ${({ onClick }) =>
    onClick &&
    `
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }
    
    &:active {
      transform: translateY(0);
    }
  `}
`

const Chip: React.FC<ChipProps> = ({
  children,
  variant = 'primary',
  size = 'medium',
  onClick,
  className,
}) => {
  return (
    <StyledChip
      variant={variant}
      size={size}
      onClick={onClick}
      className={className}
    >
      {children}
    </StyledChip>
  )
}

export default Chip
